/* eslint-disable no-console */
import type { FileChange, GitProvider, Issue, IssueOptions, PullRequest, PullRequestOptions } from '../types'
import { Buffer } from 'node:buffer'
import { spawn } from 'node:child_process'
import process from 'node:process'
import { detectRequiredPackageManagers, getAllLockFilePaths, regenerateLockFile } from '../utils/lock-file'

// Match GitHub token formats (ghp_*, gho_*, ghs_*, ghu_*, ghr_*, github_pat_*)
// plus any 40+ char alphanumeric blob that looks like a credential.
const TOKEN_PATTERN = /\b(?:gh[pousr]_[A-Z0-9_]{20,}|github_pat_[A-Z0-9_]{20,}|[A-Z0-9]{40,})\b/gi

function sanitizeStderr(stderr: string, token?: string): string {
  let out = stderr.replace(TOKEN_PATTERN, '[REDACTED]')
  if (token && token.length >= 8)
    out = out.split(token).join('[REDACTED]')
  return out
}

// Minimal shapes for the GitHub REST responses buddy-bot consumes. These exist
// to replace scattered `as any` casts with narrowed types at the API boundary.
interface GitHubUser { login: string }
interface GitHubLabel { name: string }
interface GitHubRef { ref: string }
interface GitHubPullResponse {
  number: number
  title: string
  body: string | null
  head: GitHubRef
  base: GitHubRef
  state: string
  html_url: string
  created_at: string
  updated_at: string
  merged_at: string | null
  draft: boolean
  user: GitHubUser
  requested_reviewers?: GitHubUser[]
  assignees?: GitHubUser[]
  labels?: GitHubLabel[]
}

/**
 * Thrown when lockfile regeneration fails. Not a transient git error —
 * must bubble past the API fallback so we don't silently open a PR with
 * stale lockfiles.
 */
class LockfileRegenerationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'LockfileRegenerationError'
  }
}

export class GitHubProvider implements GitProvider {
  private readonly apiUrl = 'https://api.github.com'

  // In-memory cache for API responses (reduces redundant API calls within a single workflow run)
  private cache: {
    pullRequests: Map<string, { data: PullRequest[], timestamp: number }>
    issues: Map<string, { data: Issue[], timestamp: number }>
  } = {
    pullRequests: new Map(),
    issues: new Map(),
  }

  // Cache TTL in milliseconds. Kept short (2 minutes) because the cache is
  // per-instance: a different workflow run has its own cache anyway, so a long
  // TTL just risks serving stale data (e.g. missing a PR that another parallel
  // run just created). Duplicate-PR prevention is handled by the workflow-level
  // `concurrency:` group, not by extending this cache.
  private readonly cacheTTL = 2 * 60 * 1000

  constructor(
    private readonly token: string,
    private readonly owner: string,
    private readonly repo: string,
    private readonly hasWorkflowPermissions: boolean = false,
    private readonly workflowToken?: string,
  ) {}

  /**
   * Check if cached data is still valid
   */
  private isCacheValid(timestamp: number): boolean {
    return Date.now() - timestamp < this.cacheTTL
  }

  /**
   * Clear all caches (useful when data is modified)
   */
  clearCache(): void {
    this.cache.pullRequests.clear()
    this.cache.issues.clear()
  }

  /**
   * Check if a branch exists in the repository
   */
  async branchExists(branchName: string): Promise<boolean> {
    try {
      await this.apiRequest(`GET /repos/${this.owner}/${this.repo}/git/ref/heads/${branchName}`)
      return true
    }
    catch (error: any) {
      // 404 means branch doesn't exist
      if (error.message?.includes('404'))
        return false

      // For other errors, log and return false (conservative approach)
      console.warn(`⚠️ Error checking branch ${branchName}:`, error)
      return false
    }
  }

  async createBranch(branchName: string, baseBranch: string): Promise<void> {
    try {
      // Get the base branch SHA
      const baseRef = await this.apiRequest(`GET /repos/${this.owner}/${this.repo}/git/ref/heads/${baseBranch}`)
      const baseSha = baseRef.object.sha

      // Create new branch
      await this.apiRequest(`POST /repos/${this.owner}/${this.repo}/git/refs`, {
        ref: `refs/heads/${branchName}`,
        sha: baseSha,
      })

      console.log(`✅ Created branch ${branchName} from ${baseBranch}`)
    }
    catch (error) {
      console.error(`❌ Failed to create branch ${branchName}:`, error)
      throw error
    }
  }

  async commitChanges(branchName: string, message: string, files: FileChange[], baseBranch: string = 'main'): Promise<void> {
    // Try Git CLI first for better compatibility with GitHub Actions permissions
    try {
      await this.commitChangesWithGit(branchName, message, files, baseBranch)
    }
    catch (gitError) {
      // Lockfile regeneration failures must NOT fall through to the API path —
      // the API path can't regenerate lockfiles either, so it would just produce
      // a PR with an updated manifest and stale lockfile. Bubble the error up.
      if (gitError instanceof LockfileRegenerationError)
        throw gitError

      console.warn(`⚠️ Git CLI commit failed, falling back to GitHub API: ${gitError}`)
      await this.commitChangesWithAPI(branchName, message, files, baseBranch)
    }
  }

  private async commitChangesWithGit(branchName: string, message: string, files: FileChange[], baseBranch: string = 'main'): Promise<void> {
    try {
      // Handle workflow files based on token permissions
      const workflowFiles = files.filter(f => f.path.includes('.github/workflows/'))
      const nonWorkflowFiles = files.filter(f => !f.path.includes('.github/workflows/'))

      if (workflowFiles.length > 0 && !this.hasWorkflowPermissions) {
        console.warn(`⚠️ Detected ${workflowFiles.length} workflow file(s). These require elevated permissions.`)
        console.warn(`⚠️ Workflow files: ${workflowFiles.map(f => f.path).join(', ')}`)
        console.warn(`ℹ️ Workflow files will be skipped in this commit. BUDDY_BOT_TOKEN not detected or lacks workflow permissions.`)

        // If we have non-workflow files, commit just those
        if (nonWorkflowFiles.length > 0) {
          console.log(`📝 Committing ${nonWorkflowFiles.length} non-workflow files...`)
          files = nonWorkflowFiles
        }
        else {
          console.warn(`⚠️ All files are workflow files. No files will be committed in this PR.`)
          console.warn(`💡 To update workflow files, ensure BUDDY_BOT_TOKEN is set with workflow:write permissions.`)
          // Don't return early - we'll create an empty commit to avoid "No commits between branches" error
          console.log(`📝 Creating empty commit to avoid "No commits between branches" error...`)
          try {
            await this.runCommand('git', ['commit', '--allow-empty', '-m', 'Workflow files require elevated permissions - no changes committed'])
            console.log(`✅ Created empty commit for workflow-only PR`)
          }
          catch (error) {
            console.warn(`⚠️ Failed to create empty commit: ${error}`)
            // Try to create a minimal README update instead
            try {
              const readmePath = 'README.md'
              const readmeFile = Bun.file(readmePath)
              if (await readmeFile.exists()) {
                const content = await readmeFile.text()
                const updatedContent = `${content}\n\n<!-- Updated by Buddy Bot -->\n`
                await Bun.write(readmePath, updatedContent)
                await this.runCommand('git', ['add', readmePath])
                await this.runCommand('git', ['commit', '-m', 'Update README for workflow-only PR'])
                console.log(`✅ Created README update for workflow-only PR`)
              }
            }
            catch (readmeError) {
              console.error(`❌ Failed to create any commit: ${readmeError}`)
            }
          }
          return
        }
      }
      else if (workflowFiles.length > 0) {
        console.log(`✅ Including ${workflowFiles.length} workflow file(s) with elevated permissions`)
      }

      // Configure Git identity to ensure github-actions[bot] attribution
      try {
        await this.runCommand('git', ['config', 'user.name', 'github-actions[bot]'])
        await this.runCommand('git', ['config', 'user.email', '41898282+github-actions[bot]@users.noreply.github.com'])
        console.log('✅ Git identity configured for github-actions[bot]')
      }
      catch (error) {
        console.warn('⚠️ Failed to configure Git identity:', error)
        // Continue anyway as it might already be configured
      }

      // Fetch latest changes
      await this.runCommand('git', ['fetch', 'origin'])

      // Renovate-style branch recreation: reset the PR branch to the base branch tip,
      // then apply dependency file changes fresh on top. This completely eliminates
      // merge conflicts because there's nothing to merge — we start from a clean base
      // and just write the dependency file changes on top.
      console.log(`🔄 Recreating ${branchName} from origin/${baseBranch} (Renovate-style)...`)

      // Ensure we're on the PR branch (create if it doesn't exist)
      try {
        await this.runCommand('git', ['checkout', branchName])
      }
      catch {
        try {
          await this.runCommand('git', ['checkout', '-b', branchName, `origin/${branchName}`])
        }
        catch {
          await this.runCommand('git', ['checkout', '-b', branchName])
        }
      }

      // Reset the PR branch to the base branch tip — this is the key Renovate-style move.
      // Instead of merging main into the PR branch (which causes conflicts),
      // we simply point the branch at main's HEAD and apply our changes fresh.
      await this.runCommand('git', ['reset', '--hard', `origin/${baseBranch}`])
      await this.runCommand('git', ['clean', '-fd'])
      console.log(`✅ Reset ${branchName} to origin/${baseBranch}`)

      // Apply file changes
      for (const file of files) {
        const cleanPath = file.path.replace(/^\.\//, '').replace(/^\/+/, '')

        // Safety check: prevent writing to sensitive files during tests
        if (process.env.NODE_ENV === 'test' || process.env.BUN_ENV === 'test') {
          const sensitiveFiles = ['package.json', 'bun.lock', 'bun.lockb', 'package-lock.json', 'yarn.lock']
          if (sensitiveFiles.includes(cleanPath) && file.content === '{"name":"x"}') {
            console.warn(`⚠️ Skipping test file write to ${cleanPath} to prevent overwriting project files`)
            continue
          }
        }

        if (file.type === 'delete') {
          try {
            await this.runCommand('git', ['rm', cleanPath])
          }
          catch {
            // File might not exist, that's ok for delete operations
          }
        }
        else {
          // Bun.write creates parent directories automatically and is async
          await Bun.write(cleanPath, file.content)
          await this.runCommand('git', ['add', cleanPath])
        }
      }

      // Regenerate lock files after manifest changes (skip in test environments).
      // We HARD-FAIL if regeneration fails for any required manager: a PR with an
      // updated manifest but stale lockfile is a correctness bug — consumers will
      // install the wrong versions. Fail loudly so the caller can retry or
      // investigate instead of silently opening a broken PR.
      const isTestEnv = process.env.NODE_ENV === 'test' || process.env.BUN_ENV === 'test'
      if (!isTestEnv) {
        const updatedPaths = files.map(f => f.path.replace(/^\.\//, '').replace(/^\/+/, ''))
        const requiredManagers = detectRequiredPackageManagers(updatedPaths)

        if (requiredManagers.length > 0) {
          console.log(`🔒 Regenerating lock files for: ${requiredManagers.join(', ')}`)
          const cwd = process.cwd()
          const failures: string[] = []

          for (const manager of requiredManagers) {
            try {
              const result = await regenerateLockFile(manager, cwd)
              if (result.success) {
                // Stage any regenerated lock files
                for (const lockFile of getAllLockFilePaths()) {
                  try {
                    await this.runCommand('git', ['add', lockFile])
                  }
                  catch {
                    // Lock file may not exist for this manager, that's fine
                  }
                }
              }
              else {
                failures.push(`${manager}: ${result.message}`)
              }
            }
            catch (lockError) {
              failures.push(`${manager}: ${lockError instanceof Error ? lockError.message : String(lockError)}`)
            }
          }

          if (failures.length > 0) {
            const detail = failures.map(f => `  - ${f}`).join('\n')
            throw new LockfileRegenerationError(
              `Lock file regeneration failed; refusing to open PR with stale lockfiles.\n${detail}`,
            )
          }
        }
      }

      // Check if there are changes to commit against the current branch tip
      const status = await this.runCommand('git', ['status', '--porcelain'])
      if (status.trim()) {
        // Commit changes
        await this.runCommand('git', ['commit', '-m', message])

        // Force-push is required because we recreated the branch from base (Renovate-style).
        // Use --force-with-lease for safety — it will fail if someone else pushed to the
        // branch concurrently, preventing accidental overwrites.
        // Use workflow token for push if workflow files are included (needs elevated permissions).
        const pushToken = (workflowFiles.length > 0 && this.hasWorkflowPermissions)
          ? this.getEffectiveToken(true)
          : undefined
        await this.runCommand('git', ['push', 'origin', branchName, '--force-with-lease'], pushToken)

        console.log(`✅ Successfully recreated ${branchName} with fresh changes from ${baseBranch}: ${message}`)
      }
      else {
        // SAFETY: Do NOT push when there are no file changes after resetting to base.
        // If we push now, the branch would be at the exact same commit as main,
        // and GitHub would auto-close the PR thinking it was merged.
        // This can happen when:
        //   1. The dependency changes were already merged to main
        //   2. commitChanges was called with an empty file list
        // In both cases, the right thing is to leave the branch alone.
        console.log(`ℹ️ No file changes after resetting to ${baseBranch} — skipping push to prevent PR auto-close`)
      }
    }
    catch (error) {
      console.error(`❌ Failed to commit changes to ${branchName} with Git CLI:`, error)
      throw error
    }
  }

  private async commitChangesWithAPI(branchName: string, message: string, files: FileChange[], baseBranch: string = 'main'): Promise<void> {
    try {
      // Handle workflow files based on token permissions
      const workflowFiles = files.filter(f => f.path.includes('.github/workflows/'))
      const nonWorkflowFiles = files.filter(f => !f.path.includes('.github/workflows/'))

      if (workflowFiles.length > 0 && !this.hasWorkflowPermissions) {
        console.warn(`⚠️ Detected ${workflowFiles.length} workflow file(s). These require elevated permissions.`)
        console.warn(`⚠️ Workflow files: ${workflowFiles.map(f => f.path).join(', ')}`)
        console.warn(`ℹ️ Workflow files will be skipped in this commit. Consider using a GitHub App with workflow permissions for workflow updates.`)

        // If we have non-workflow files, commit just those
        if (nonWorkflowFiles.length > 0) {
          console.log(`📝 Committing ${nonWorkflowFiles.length} non-workflow files...`)
          files = nonWorkflowFiles
        }
        else {
          console.warn(`⚠️ All files are workflow files. No files will be committed in this PR.`)
          console.warn(`💡 To update workflow files, consider using a GitHub App with appropriate permissions.`)
          return // Exit early if no non-workflow files to commit
        }
      }
      else if (workflowFiles.length > 0) {
        console.log(`✅ Including ${workflowFiles.length} workflow file(s) with elevated permissions`)
      }

      // Note: Lock files cannot be regenerated when committing via the API path
      // because there is no local filesystem to run install commands against.
      const hasManifestFiles = files.some(f =>
        f.path.endsWith('package.json') || f.path.endsWith('composer.json'),
      )
      if (hasManifestFiles) {
        console.warn(`⚠️ Committing manifest files via API — lock files will not be updated.`)
        console.warn(`   Lock file regeneration requires the Git CLI commit path (local filesystem).`)
      }

      // Renovate-style: base the new commit on the base branch (e.g. main), not the PR branch.
      // This recreates the branch from scratch, eliminating any merge conflicts.
      console.log(`🔄 Recreating ${branchName} from ${baseBranch} via API (Renovate-style)...`)
      const baseRef = await this.apiRequest(`GET /repos/${this.owner}/${this.repo}/git/ref/heads/${baseBranch}`)
      const baseSha = baseRef.object.sha

      // Get base branch tree
      const baseCommit = await this.apiRequest(`GET /repos/${this.owner}/${this.repo}/git/commits/${baseSha}`)
      const baseTreeSha = baseCommit.tree.sha

      // Create new tree with file changes
      const tree = []
      for (const file of files) {
        // Ensure path doesn't start with ./ or have leading slashes (GitHub API requires clean relative paths)
        const cleanPath = file.path.replace(/^\.\//, '').replace(/^\/+/, '')

        if (file.type === 'delete') {
          tree.push({
            path: cleanPath,
            mode: '100644',
            type: 'blob',
            sha: null,
          })
        }
        else {
          // Create blob for file content
          const blob = await this.apiRequest(`POST /repos/${this.owner}/${this.repo}/git/blobs`, {
            content: Buffer.from(file.content).toString('base64'),
            encoding: 'base64',
          })

          tree.push({
            path: cleanPath,
            mode: '100644',
            type: 'blob',
            sha: blob.sha,
          })
        }
      }

      const newTree = await this.apiRequest(`POST /repos/${this.owner}/${this.repo}/git/trees`, {
        base_tree: baseTreeSha,
        tree,
      })

      // SAFETY: If the tree matches base, the dependency changes are already in main.
      // Do NOT update the branch ref — pointing it at main's SHA would make GitHub
      // auto-close the PR thinking it was merged.
      if (newTree.sha === baseTreeSha) {
        console.log(`ℹ️ No file changes relative to ${baseBranch} — skipping push to prevent PR auto-close`)
        return
      }

      // Create new commit parented to the base branch SHA (Renovate-style: single commit on top of base)
      const newCommit = await this.apiRequest(`POST /repos/${this.owner}/${this.repo}/git/commits`, {
        message,
        tree: newTree.sha,
        parents: [baseSha],
        author: {
          name: 'github-actions[bot]',
          email: '41898282+github-actions[bot]@users.noreply.github.com',
        },
        committer: {
          name: 'github-actions[bot]',
          email: '41898282+github-actions[bot]@users.noreply.github.com',
        },
      })

      // Force-update branch reference (required since we're recreating from base branch)
      await this.apiRequest(`PATCH /repos/${this.owner}/${this.repo}/git/refs/heads/${branchName}`, {
        sha: newCommit.sha,
        force: true,
      })

      console.log(`✅ Committed changes to ${branchName}: ${message}`)
    }
    catch (error) {
      console.error(`❌ Failed to commit changes to ${branchName}:`, error)
      throw error
    }
  }

  async createPullRequest(options: PullRequestOptions): Promise<PullRequest> {
    // Try GitHub CLI first as it might have better permission handling
    try {
      return await this.createPullRequestWithCLI(options)
    }
    catch (cliError) {
      console.warn(`⚠️ GitHub CLI failed, falling back to API: ${cliError}`)
      return await this.createPullRequestWithAPI(options)
    }
  }

  /**
   * Create pull request using GitHub CLI
   */
  private async createPullRequestWithCLI(options: PullRequestOptions): Promise<PullRequest> {
    try {
      // Prepare the command
      const args = [
        'pr',
        'create',
        '--title',
        options.title,
        '--body',
        options.body,
        '--head',
        options.head,
        '--base',
        options.base,
      ]

      if (options.draft) {
        args.push('--draft')
      }

      if (options.reviewers && options.reviewers.length > 0) {
        console.log(`🔍 Adding reviewers via CLI: ${options.reviewers.join(', ')}`)
        args.push('--reviewer', options.reviewers.join(','))
      }

      if (options.assignees && options.assignees.length > 0) {
        args.push('--assignee', options.assignees.join(','))
      }

      // Note: We don't add labels via CLI to avoid failures when labels don't exist
      // Labels will be added via API after PR creation if needed

      // Execute GitHub CLI command
      const result = await this.runCommand('gh', args)

      // Parse the PR URL from the output (GitHub CLI returns the PR URL)
      const prUrlMatch = result.match(/https:\/\/github\.com\/[^/]+\/[^/]+\/pull\/(\d+)/)
      if (!prUrlMatch) {
        throw new Error('Failed to parse PR number from GitHub CLI output')
      }

      const prNumber = Number.parseInt(prUrlMatch[1])
      const prUrl = prUrlMatch[0]

      console.log(`✅ Created PR #${prNumber}: ${options.title}`)

      // Add labels via API after PR creation to handle missing labels gracefully
      if (options.labels && options.labels.length > 0) {
        try {
          await this.apiRequest(`POST /repos/${this.owner}/${this.repo}/issues/${prNumber}/labels`, {
            labels: options.labels,
          })
          console.log(`✅ Added labels to PR #${prNumber}: ${options.labels.join(', ')}`)
        }
        catch (labelError) {
          console.warn(`⚠️ Failed to add labels: ${labelError}`)
          // Try to add labels one by one to handle missing labels gracefully
          for (const label of options.labels) {
            try {
              await this.apiRequest(`POST /repos/${this.owner}/${this.repo}/issues/${prNumber}/labels`, {
                labels: [label],
              })
            }
            catch (singleLabelError) {
              console.warn(`⚠️ Failed to add label '${label}': ${singleLabelError}`)
            }
          }
        }
      }

      return {
        number: prNumber,
        title: options.title,
        body: options.body,
        head: options.head,
        base: options.base,
        state: 'open',
        url: prUrl,
        createdAt: new Date(),
        updatedAt: new Date(),
        author: 'github-actions[bot]',
        reviewers: options.reviewers || [],
        assignees: options.assignees || [],
        labels: options.labels || [],
        draft: options.draft || false,
      }
    }
    catch (error) {
      console.error(`❌ Failed to create PR with GitHub CLI: ${options.title}`, error)
      throw error
    }
  }

  /**
   * Create pull request using GitHub API (fallback)
   */
  private async createPullRequestWithAPI(options: PullRequestOptions): Promise<PullRequest> {
    try {
      const response = await this.apiRequest(`POST /repos/${this.owner}/${this.repo}/pulls`, {
        title: options.title,
        body: options.body,
        head: options.head,
        base: options.base,
        draft: options.draft || false,
      })

      // Add reviewers if specified
      if (options.reviewers && options.reviewers.length > 0) {
        try {
          console.log(`🔍 Adding reviewers to PR #${response.number}: ${options.reviewers.join(', ')}`)
          await this.apiRequest(`POST /repos/${this.owner}/${this.repo}/pulls/${response.number}/requested_reviewers`, {
            reviewers: options.reviewers,
            team_reviewers: options.teamReviewers || [],
          })
          console.log(`✅ Successfully added reviewers: ${options.reviewers.join(', ')}`)
        }
        catch (reviewerError) {
          console.error(`❌ Failed to add reviewers: ${reviewerError}`)
          console.error(`   Reviewers: ${options.reviewers.join(', ')}`)
          console.error(`   Repository: ${this.owner}/${this.repo}`)
          console.error(`   PR: #${response.number}`)
        }
      }

      // Add assignees if specified
      if (options.assignees && options.assignees.length > 0) {
        try {
          await this.apiRequest(`POST /repos/${this.owner}/${this.repo}/issues/${response.number}/assignees`, {
            assignees: options.assignees,
          })
        }
        catch (assigneeError) {
          console.warn(`⚠️ Failed to add assignees: ${assigneeError}`)
        }
      }

      // Add labels if specified
      if (options.labels && options.labels.length > 0) {
        try {
          await this.apiRequest(`POST /repos/${this.owner}/${this.repo}/issues/${response.number}/labels`, {
            labels: options.labels,
          })
        }
        catch (labelError) {
          console.warn(`⚠️ Failed to add labels: ${labelError}`)
          // Try to add labels one by one to handle missing labels gracefully
          for (const label of options.labels) {
            try {
              await this.apiRequest(`POST /repos/${this.owner}/${this.repo}/issues/${response.number}/labels`, {
                labels: [label],
              })
            }
            catch (singleLabelError) {
              console.warn(`⚠️ Failed to add label '${label}': ${singleLabelError}`)
            }
          }
        }
      }

      console.log(`✅ Created PR #${response.number}: ${options.title}`)

      return {
        number: response.number,
        title: response.title,
        body: response.body || '',
        head: response.head.ref,
        base: response.base.ref,
        state: response.state as 'open' | 'closed' | 'merged',
        url: response.html_url,
        createdAt: new Date(response.created_at),
        updatedAt: new Date(response.updated_at),
        author: response.user.login,
        reviewers: options.reviewers || [],
        assignees: options.assignees || [],
        labels: options.labels || [],
        draft: response.draft,
      }
    }
    catch (error) {
      console.error(`❌ Failed to create PR with API: ${options.title}`, error)
      throw error
    }
  }

  /**
   * Get the effective token for a given operation.
   * Uses the workflow token (PAT) for operations that need elevated permissions,
   * and the primary token (GITHUB_TOKEN) for everything else.
   */
  private getEffectiveToken(requireWorkflowPermissions = false): string {
    if (requireWorkflowPermissions && this.workflowToken)
      return this.workflowToken
    return this.token
  }

  /**
   * Run a command and return its output
   */
  async runCommand(command: string, args: string[], tokenOverride?: string): Promise<string> {
    const effectiveToken = tokenOverride || this.token
    return new Promise((resolve, reject) => {
      const child = spawn(command, args, {
        stdio: 'pipe',
        env: {
          ...process.env,
          GITHUB_TOKEN: effectiveToken,
          GH_TOKEN: effectiveToken,
        },
      })

      let stdout = ''
      let stderr = ''

      child.stdout?.on('data', (data) => {
        stdout += data.toString()
      })

      child.stderr?.on('data', (data) => {
        stderr += data.toString()
      })

      child.on('close', (code) => {
        if (code === 0) {
          resolve(stdout)
        }
        else {
          // Scrub any token-shaped strings (including the effective token itself)
          // before surfacing stderr — stderr is logged and can end up in CI output.
          reject(new Error(`Command failed with code ${code}: ${sanitizeStderr(stderr, effectiveToken)}`))
        }
      })

      child.on('error', (error) => {
        reject(error)
      })
    })
  }

  async getPullRequests(state: 'open' | 'closed' | 'all' = 'open'): Promise<PullRequest[]> {
    // Check cache first
    const cacheKey = `${state}`
    const cached = this.cache.pullRequests.get(cacheKey)
    if (cached && this.isCacheValid(cached.timestamp)) {
      console.log(`📦 Using cached PRs (state: ${state}, ${cached.data.length} PRs)`)
      return cached.data
    }

    try {
      // Use apiRequestWithRetry for rate limit handling
      const response = await this.apiRequestWithRetry(`GET /repos/${this.owner}/${this.repo}/pulls?state=${state}&per_page=100`)

      const prs: PullRequest[] = (response as GitHubPullResponse[]).map(pr => ({
        number: pr.number,
        title: pr.title,
        body: pr.body ?? '',
        head: pr.head.ref,
        base: pr.base.ref,
        state: pr.state as PullRequest['state'],
        url: pr.html_url,
        createdAt: new Date(pr.created_at),
        updatedAt: new Date(pr.updated_at),
        mergedAt: pr.merged_at ? new Date(pr.merged_at) : undefined,
        author: pr.user.login,
        reviewers: pr.requested_reviewers?.map(r => r.login) ?? [],
        assignees: pr.assignees?.map(a => a.login) ?? [],
        labels: pr.labels?.map(l => l.name) ?? [],
        draft: pr.draft,
      }))

      // Cache the result
      this.cache.pullRequests.set(cacheKey, { data: prs, timestamp: Date.now() })
      console.log(`✅ Fetched and cached ${prs.length} PRs (state: ${state})`)

      return prs
    }
    catch (error) {
      console.error(`❌ Failed to get PRs:`, error)
      throw error
    }
  }

  async updatePullRequest(prNumber: number, options: Partial<PullRequestOptions>): Promise<PullRequest> {
    // Invalidate PR cache since we're modifying data
    this.cache.pullRequests.clear()

    try {
      const updateData: any = {}
      if (options.title)
        updateData.title = options.title
      if (options.body)
        updateData.body = options.body
      if (options.base)
        updateData.base = options.base
      if (options.draft !== undefined)
        updateData.draft = options.draft

      const response = await this.apiRequest(`PATCH /repos/${this.owner}/${this.repo}/pulls/${prNumber}`, updateData)

      // Update labels if specified
      if (options.labels && options.labels.length > 0) {
        try {
          await this.apiRequest(`PUT /repos/${this.owner}/${this.repo}/issues/${prNumber}/labels`, {
            labels: options.labels,
          })
          console.log(`✅ Updated labels for PR #${prNumber}: ${options.labels.join(', ')}`)
        }
        catch (labelError) {
          console.warn(`⚠️ Failed to update labels for PR #${prNumber}: ${labelError}`)
          // Try to add labels one by one to handle missing labels gracefully
          for (const label of options.labels) {
            try {
              await this.apiRequest(`POST /repos/${this.owner}/${this.repo}/issues/${prNumber}/labels`, {
                labels: [label],
              })
            }
            catch (singleLabelError) {
              console.warn(`⚠️ Failed to add label '${label}' to PR #${prNumber}: ${singleLabelError}`)
            }
          }
        }
      }

      // Update reviewers if specified
      if (options.reviewers && options.reviewers.length > 0) {
        try {
          console.log(`🔍 Adding reviewers to existing PR #${prNumber}: ${options.reviewers.join(', ')}`)
          await this.apiRequest(`POST /repos/${this.owner}/${this.repo}/pulls/${prNumber}/requested_reviewers`, {
            reviewers: options.reviewers,
            team_reviewers: options.teamReviewers || [],
          })
          console.log(`✅ Updated reviewers for PR #${prNumber}: ${options.reviewers.join(', ')}`)
        }
        catch (reviewerError) {
          console.error(`❌ Failed to update reviewers for PR #${prNumber}: ${reviewerError}`)
          console.error(`   Reviewers: ${options.reviewers.join(', ')}`)
          console.error(`   Repository: ${this.owner}/${this.repo}`)
        }
      }

      // Update assignees if specified
      if (options.assignees && options.assignees.length > 0) {
        try {
          // Use GitHub CLI for assignees (more reliable with permissions)
          await this.runCommand('gh', ['issue', 'edit', prNumber.toString(), '--add-assignee', options.assignees.join(',')])
          console.log(`✅ Updated assignees for PR #${prNumber}: ${options.assignees.join(', ')}`)
        }
        catch (assigneeError) {
          console.warn(`⚠️ Failed to update assignees for PR #${prNumber}: ${assigneeError}`)
        }
      }

      console.log(`✅ Updated PR #${prNumber}`)

      return {
        number: response.number,
        title: response.title,
        body: response.body || '',
        head: response.head.ref,
        base: response.base.ref,
        state: response.state,
        url: response.html_url,
        createdAt: new Date(response.created_at),
        updatedAt: new Date(response.updated_at),
        author: response.user.login,
        reviewers: [],
        assignees: [],
        labels: options.labels || [],
        draft: response.draft,
      }
    }
    catch (error) {
      console.error(`❌ Failed to update PR #${prNumber}:`, error)
      throw error
    }
  }

  async closePullRequest(prNumber: number): Promise<void> {
    // Invalidate PR cache since we're modifying data
    this.cache.pullRequests.clear()

    try {
      await this.apiRequest(`PATCH /repos/${this.owner}/${this.repo}/pulls/${prNumber}`, {
        state: 'closed',
      })
      console.log(`✅ Closed PR #${prNumber}`)
      // NOTE: Branch cleanup is the caller's responsibility.
      // Automatically deleting the branch here prevents reopening PRs later.
    }
    catch (error) {
      console.error(`❌ Failed to close PR #${prNumber}:`, error)
      throw error
    }
  }

  async reopenPullRequest(prNumber: number): Promise<void> {
    // Invalidate PR cache since we're modifying data
    this.cache.pullRequests.clear()

    try {
      await this.apiRequest(`PATCH /repos/${this.owner}/${this.repo}/pulls/${prNumber}`, {
        state: 'open',
      })
      console.log(`✅ Reopened PR #${prNumber}`)
    }
    catch (error) {
      console.error(`❌ Failed to reopen PR #${prNumber}:`, error)
      throw error
    }
  }

  async createComment(prNumber: number, comment: string): Promise<void> {
    try {
      await this.apiRequest(`POST /repos/${this.owner}/${this.repo}/issues/${prNumber}/comments`, {
        body: comment,
      })
      console.log(`💬 Added comment to PR #${prNumber}`)
    }
    catch (error) {
      console.error(`❌ Failed to add comment to PR #${prNumber}:`, error)
      throw error
    }
  }

  async mergePullRequest(prNumber: number, strategy: 'merge' | 'squash' | 'rebase' = 'merge'): Promise<void> {
    try {
      const mergeMethod = strategy === 'rebase' ? 'rebase' : strategy === 'squash' ? 'squash' : 'merge'

      // Get PR details to know the branch name for cleanup
      const prDetails = await this.apiRequest(`GET /repos/${this.owner}/${this.repo}/pulls/${prNumber}`)
      const branchName = prDetails.head.ref

      await this.apiRequest(`PUT /repos/${this.owner}/${this.repo}/pulls/${prNumber}/merge`, {
        merge_method: mergeMethod,
      })

      console.log(`✅ Merged PR #${prNumber} using ${strategy}`)

      // Clean up the branch after successful merge
      try {
        await this.deleteBranch(branchName)
        console.log(`🧹 Cleaned up branch ${branchName} after merge`)
      }
      catch (cleanupError) {
        console.warn(`⚠️ Failed to clean up branch ${branchName}:`, cleanupError)
      }
    }
    catch (error) {
      console.error(`❌ Failed to merge PR #${prNumber}:`, error)
      throw error
    }
  }

  /**
   * Delete a branch using pure git commands (no API calls)
   */
  async deleteBranch(branchName: string): Promise<void> {
    try {
      // Use pure git to delete the remote branch (no API calls!)
      await this.runCommand('git', ['push', 'origin', '--delete', branchName])
      console.log(`✅ Deleted branch ${branchName} via git`)
    }
    catch (error) {
      // If git push fails, it might be because the branch doesn't exist remotely
      // or we don't have push permissions. Try to delete locally and ignore errors.
      try {
        // Also delete local tracking branch if it exists
        await this.runCommand('git', ['branch', '-D', branchName])
        console.log(`✅ Deleted local branch ${branchName}`)
      }
      catch {
        // Ignore local deletion errors - branch might not exist locally
      }

      console.warn(`⚠️ Failed to delete remote branch ${branchName}:`, error)
      // Don't throw - branch deletion failures are not critical
    }
  }

  /**
   * Get all buddy-bot branches from the repository using local git commands
   */
  async getBuddyBotBranches(): Promise<Array<{ name: string, sha: string, lastCommitDate: Date }>> {
    try {
      // Use local git to get all remote branches
      const remoteBranchesOutput = await this.runCommand('git', ['branch', '-r', '--format=%(refname:short) %(objectname) %(committerdate:iso8601)'])

      const branches: Array<{ name: string, sha: string, lastCommitDate: Date }> = []

      for (const line of remoteBranchesOutput.split('\n')) {
        const trimmed = line.trim()
        if (!trimmed)
          continue

        const parts = trimmed.split(' ')
        if (parts.length < 3)
          continue

        const fullBranchName = parts[0] // e.g., "origin/buddy-bot/update-deps"
        const sha = parts[1]
        const dateStr = parts.slice(2).join(' ') // Join back in case date has spaces

        // Extract just the branch name without remote prefix
        const branchName = fullBranchName.replace(/^origin\//, '')

        // Only include buddy-bot branches
        if (!branchName.startsWith('buddy-bot/'))
          continue

        try {
          const lastCommitDate = new Date(dateStr)
          branches.push({
            name: branchName,
            sha,
            lastCommitDate,
          })
        }
        catch {
          console.warn(`⚠️ Failed to parse date for branch ${branchName}: ${dateStr}`)
          branches.push({
            name: branchName,
            sha,
            lastCommitDate: new Date(0), // Fallback to epoch
          })
        }
      }

      console.log(`🔍 Found ${branches.length} buddy-bot branches using local git`)
      return branches
    }
    catch (error) {
      console.warn('⚠️ Failed to fetch buddy-bot branches via git, falling back to API:', error)

      // Fallback to API method if git fails
      return this.getBuddyBotBranchesViaAPI()
    }
  }

  /**
   * Fallback method to get buddy-bot branches via API (original implementation)
   */
  private async getBuddyBotBranchesViaAPI(): Promise<Array<{ name: string, sha: string, lastCommitDate: Date }>> {
    try {
      // Fetch all branches with pagination
      let allBranches: any[] = []
      let page = 1
      const perPage = 100

      while (true) {
        const branches = await this.apiRequest(`GET /repos/${this.owner}/${this.repo}/branches?per_page=${perPage}&page=${page}`)

        if (!branches || branches.length === 0) {
          break
        }

        allBranches = allBranches.concat(branches)

        // If we got less than perPage results, we've reached the end
        if (branches.length < perPage) {
          break
        }

        page++
      }

      console.log(`🔍 Found ${allBranches.length} total branches in repository`)

      // Filter for buddy-bot branches
      const buddyBranches = allBranches.filter((branch: any) => branch.name.startsWith('buddy-bot/'))
      console.log(`🤖 Found ${buddyBranches.length} buddy-bot branches`)

      // Get detailed info for each branch including last commit date
      const branchDetails = await Promise.all(
        buddyBranches.map(async (branch: any) => {
          try {
            const commit = await this.apiRequest(`GET /repos/${this.owner}/${this.repo}/commits/${branch.commit.sha}`)
            return {
              name: branch.name,
              sha: branch.commit.sha,
              lastCommitDate: new Date(commit.commit.committer.date),
            }
          }
          catch (error) {
            console.warn(`⚠️ Failed to get commit info for branch ${branch.name}:`, error)
            return {
              name: branch.name,
              sha: branch.commit.sha,
              lastCommitDate: new Date(0), // Fallback to epoch
            }
          }
        }),
      )

      return branchDetails
    }
    catch (error) {
      console.warn('⚠️ Failed to fetch buddy-bot branches:', error)
      return []
    }
  }

  /**
   * Get all buddy-bot branches that don't have associated open PRs
   */
  async getOrphanedBuddyBotBranches(): Promise<Array<{ name: string, sha: string, lastCommitDate: Date }>> {
    try {
      const buddyBranches = await this.getBuddyBotBranches()

      const prBranches = await this.getOpenPRBranches()

      // Filter out branches that have active PRs
      const orphanedBranches = buddyBranches.filter(branch => !prBranches.has(branch.name))

      return orphanedBranches
    }
    catch (error) {
      console.warn('⚠️ Failed to identify orphaned branches:', error)
      return []
    }
  }

  /**
   * Tracks whether the most recent open-PR detection succeeded authoritatively
   * (via the GitHub API). Consumed by cleanupStaleBranches to decide between
   * "delete every orphan" and "fall back to age-based protection".
   */
  private prDetectionSuccessful = false

  /**
   * Get the set of buddy-bot branches that currently have open PRs.
   *
   * Uses the GitHub REST API as the authoritative source. Earlier versions
   * scraped the PR HTML page for `State--open` CSS classes, which silently
   * broke when GitHub removed those classes — every open PR was misreported
   * as closed, causing cleanupStaleBranches to delete the live PR's branch
   * (which in turn auto-closed the PR).
   */
  private async getOpenPRBranches(): Promise<Set<string>> {
    this.prDetectionSuccessful = false

    try {
      const openPRs = await this.getPullRequests('open')
      const protectedBranches = new Set<string>(
        openPRs
          .map(pr => pr.head)
          .filter(head => head.startsWith('buddy-bot/')),
      )

      console.log(`🔍 GitHub API reports ${openPRs.length} open PR(s); ${protectedBranches.size} are buddy-bot branches`)
      console.log(`🛡️ Protecting ${protectedBranches.size} branches with confirmed open PRs`)
      this.prDetectionSuccessful = true
      return protectedBranches
    }
    catch (error) {
      console.warn('⚠️ Failed to fetch open PRs via API, falling back to age-based protection:', error)

      // Conservative fallback: protect branches less than 30 days old
      try {
        const allBuddyBranches = await this.getBuddyBotBranches()
        const thirtyDaysAgo = new Date()
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

        const conservativeBranches = new Set<string>()
        for (const branch of allBuddyBranches) {
          if (branch.lastCommitDate > thirtyDaysAgo) {
            conservativeBranches.add(branch.name)
          }
        }

        console.log(`🛡️ Conservative fallback: protecting ${conservativeBranches.size} branches newer than 30 days`)
        return conservativeBranches
      }
      catch {
        // Ultimate fallback: leave prDetectionSuccessful=false so cleanupStaleBranches
        // applies its age-based filter against whatever branches it can enumerate.
        console.log('⚠️ Could not enumerate branches for age-based fallback')
        return new Set<string>()
      }
    }
  }

  /**
   * Clean up orphaned buddy-bot branches (with optional age filter for fallback scenarios)
   */
  async cleanupStaleBranches(olderThanDays = 7, dryRun = false): Promise<{ deleted: string[], failed: string[] }> {
    console.log(`🔍 Looking for buddy-bot branches without open PRs...`)

    const orphanedBranches = await this.getOrphanedBuddyBotBranches()
    console.log(`🔍 Found ${orphanedBranches.length} orphaned buddy-bot branches (no associated open PRs)`)

    // When PR detection succeeded authoritatively, every orphan is a true orphan.
    // If detection fell back to age-based protection, only delete branches older
    // than the configured threshold to avoid wiping out branches whose live PRs
    // we couldn't verify.
    let branchesToDelete = orphanedBranches

    if (this.prDetectionSuccessful) {
      console.log(`🎯 PR detection successful - cleaning up ALL ${branchesToDelete.length} orphaned branches (any age)`)
    }
    else {
      const cutoffDate = new Date()
      cutoffDate.setDate(cutoffDate.getDate() - olderThanDays)
      branchesToDelete = orphanedBranches.filter(branch => branch.lastCommitDate < cutoffDate)
      console.log(`⚠️ PR detection failed - only deleting branches older than ${olderThanDays} days`)
      console.log(`🔍 Found ${branchesToDelete.length} stale buddy-bot branches (older than ${olderThanDays} days)`)
    }

    // Show some examples of what we found
    if (branchesToDelete.length > 0) {
      console.log('📋 Sample of branches to delete:')
      branchesToDelete.slice(0, 5).forEach((branch) => {
        const daysOld = Math.floor((Date.now() - branch.lastCommitDate.getTime()) / (1000 * 60 * 60 * 24))
        console.log(`  - ${branch.name} (${daysOld} days old)`)
      })
      if (branchesToDelete.length > 5) {
        console.log(`  ... and ${branchesToDelete.length - 5} more`)
      }
    }

    if (branchesToDelete.length === 0) {
      console.log('✅ No branches to clean up!')
      return { deleted: [], failed: [] }
    }

    const staleBranches = branchesToDelete

    if (dryRun) {
      console.log('🔍 [DRY RUN] Would delete the following branches:')
      staleBranches.forEach((branch) => {
        const daysOld = Math.floor((Date.now() - branch.lastCommitDate.getTime()) / (1000 * 60 * 60 * 24))
        console.log(`  - ${branch.name} (${daysOld} days old, last commit: ${branch.lastCommitDate.toISOString()})`)
      })
      return { deleted: staleBranches.map(b => b.name), failed: [] }
    }

    const deleted: string[] = []
    const failed: string[] = []

    console.log(`🧹 Cleaning up ${staleBranches.length} stale branches...`)

    // Delete branches in smaller batches with longer delays to avoid rate limiting
    const batchSize = 5 // Reduced from 10 to be more conservative
    for (let i = 0; i < staleBranches.length; i += batchSize) {
      const batch = staleBranches.slice(i, i + batchSize)
      const batchNumber = Math.floor(i / batchSize) + 1
      const totalBatches = Math.ceil(staleBranches.length / batchSize)

      console.log(`🔄 Processing batch ${batchNumber}/${totalBatches} (${batch.length} branches)`)

      // Process branches sequentially within batch to avoid overwhelming the API
      for (const branch of batch) {
        try {
          await this.deleteBranch(branch.name)
          deleted.push(branch.name)
          console.log(`✅ Deleted: ${branch.name}`)
        }
        catch (error) {
          failed.push(branch.name)
          console.warn(`❌ Failed to delete ${branch.name}:`, error)
        }

        // Small delay between individual deletions within batch
        await new Promise(resolve => setTimeout(resolve, 200))
      }

      // Longer delay between batches to be respectful of API limits
      if (i + batchSize < staleBranches.length) {
        const delay = 3000 // 3 seconds between batches
        console.log(`⏳ Waiting ${delay / 1000} seconds before next batch...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }

    console.log(`🎉 Cleanup complete!`)
    console.log(`  ✅ Successfully deleted: ${deleted.length} branches`)
    console.log(`  ❌ Failed to delete: ${failed.length} branches`)

    if (failed.length > 0) {
      console.log('❌ Failed branches:')
      failed.forEach(branch => console.log(`  - ${branch}`))
    }

    return { deleted, failed }
  }

  /**
   * Make authenticated API request to GitHub
   */
  private async apiRequest(endpoint: string, data?: any, tokenOverride?: string): Promise<any> {
    const [method, path] = endpoint.split(' ')
    const url = `${this.apiUrl}${path}`
    const effectiveToken = tokenOverride || this.token

    const options: RequestInit = {
      method,
      headers: {
        'Authorization': `Bearer ${effectiveToken}`,
        'Accept': 'application/vnd.github.v3+json',
        'Content-Type': 'application/json',
        'User-Agent': 'buddy-bot',
      },
    }

    if (data && (method === 'POST' || method === 'PATCH' || method === 'PUT')) {
      options.body = JSON.stringify(data)
    }

    const response = await fetch(url, options)

    if (!response.ok) {
      const errorBody = await response.text()
      const tokenHint = effectiveToken
        ? 'token present ([REDACTED])'
        : 'NO TOKEN — ensure GITHUB_TOKEN or BUDDY_BOT_TOKEN is set'
      throw new Error(
        `GitHub API error: ${response.status} ${response.statusText}\n`
        + `  URL: ${method} ${url}\n`
        + `  Auth: ${tokenHint}\n`
        + `  Repo: ${this.owner}/${this.repo}\n`
        + `${errorBody}`,
      )
    }

    if (response.headers.get('content-type')?.includes('application/json')) {
      return response.json()
    }

    return response.text()
  }

  /**
   * Make authenticated API request to GitHub with retry logic for rate limiting
   */
  private async apiRequestWithRetry(endpoint: string, data?: any, maxRetries = 3): Promise<any> {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await this.apiRequest(endpoint, data)
      }
      catch (error: any) {
        const isRateLimit = error.message?.includes('403') && error.message?.includes('rate limit')

        if (isRateLimit && attempt < maxRetries) {
          // Extract retry-after from error or use exponential backoff
          const baseDelay = 2 ** attempt * 1000 // 2s, 4s, 8s
          const jitter = Math.random() * 1000 // Add up to 1s jitter
          const delay = baseDelay + jitter

          console.log(`⏳ Rate limited, waiting ${Math.round(delay / 1000)}s before retry ${attempt}/${maxRetries}...`)
          await new Promise(resolve => setTimeout(resolve, delay))
          continue
        }

        // If not rate limit or max retries reached, throw the error
        throw error
      }
    }
  }

  async createIssue(options: IssueOptions): Promise<Issue> {
    // Invalidate issue cache since we're creating new data
    this.cache.issues.clear()

    try {
      const response = await this.apiRequestWithRetry(`POST /repos/${this.owner}/${this.repo}/issues`, {
        title: options.title,
        body: options.body,
        assignees: options.assignees || [],
        labels: options.labels || [],
        milestone: options.milestone,
      })

      console.log(`✅ Created issue #${response.number}: ${options.title}`)

      return {
        number: response.number,
        title: response.title,
        body: response.body,
        state: response.state,
        url: response.html_url,
        createdAt: new Date(response.created_at),
        updatedAt: new Date(response.updated_at),
        closedAt: response.closed_at ? new Date(response.closed_at) : undefined,
        author: response.user.login,
        assignees: response.assignees?.map((a: any) => a.login) || [],
        labels: response.labels?.map((l: any) => typeof l === 'string' ? l : l.name) || [],
        pinned: false, // GitHub API doesn't return pinned status directly
      }
    }
    catch (error) {
      console.error(`❌ Failed to create issue: ${options.title}`, error)
      throw error
    }
  }

  async getIssues(state: 'open' | 'closed' | 'all' = 'open'): Promise<Issue[]> {
    // Check cache first
    const cacheKey = `${state}`
    const cached = this.cache.issues.get(cacheKey)
    if (cached && this.isCacheValid(cached.timestamp)) {
      console.log(`📦 Using cached issues (state: ${state}, ${cached.data.length} issues)`)
      return cached.data
    }

    try {
      // Use apiRequestWithRetry for rate limit handling
      const response = await this.apiRequestWithRetry(`GET /repos/${this.owner}/${this.repo}/issues?state=${state}&sort=updated&direction=desc&per_page=100`)

      const issues = response
        .filter((issue: any) => !issue.pull_request) // Filter out PRs (they're returned as issues by GitHub API)
        .map((issue: any) => ({
          number: issue.number,
          title: issue.title,
          body: issue.body || '',
          state: issue.state,
          url: issue.html_url,
          createdAt: new Date(issue.created_at),
          updatedAt: new Date(issue.updated_at),
          closedAt: issue.closed_at ? new Date(issue.closed_at) : undefined,
          author: issue.user.login,
          assignees: issue.assignees?.map((a: any) => a.login) || [],
          labels: issue.labels?.map((l: any) => typeof l === 'string' ? l : l.name) || [],
          pinned: false, // GitHub API doesn't return pinned status directly
        }))

      // Cache the result
      this.cache.issues.set(cacheKey, { data: issues, timestamp: Date.now() })
      console.log(`✅ Fetched and cached ${issues.length} issues (state: ${state})`)

      return issues
    }
    catch (error) {
      console.error('❌ Failed to get issues:', error)
      throw error
    }
  }

  async updateIssue(issueNumber: number, options: Partial<IssueOptions>): Promise<Issue> {
    // Invalidate issue cache since we're modifying data
    this.cache.issues.clear()

    try {
      const updateData: any = {}

      if (options.title !== undefined)
        updateData.title = options.title
      if (options.body !== undefined)
        updateData.body = options.body
      if (options.assignees !== undefined)
        updateData.assignees = options.assignees
      if (options.labels !== undefined)
        updateData.labels = options.labels
      if (options.milestone !== undefined)
        updateData.milestone = options.milestone

      const response = await this.apiRequestWithRetry(`PATCH /repos/${this.owner}/${this.repo}/issues/${issueNumber}`, updateData)

      console.log(`✅ Updated issue #${issueNumber}: ${response.title}`)

      return {
        number: response.number,
        title: response.title,
        body: response.body,
        state: response.state,
        url: response.html_url,
        createdAt: new Date(response.created_at),
        updatedAt: new Date(response.updated_at),
        closedAt: response.closed_at ? new Date(response.closed_at) : undefined,
        author: response.user.login,
        assignees: response.assignees?.map((a: any) => a.login) || [],
        labels: response.labels?.map((l: any) => typeof l === 'string' ? l : l.name) || [],
        pinned: false, // GitHub API doesn't return pinned status directly
      }
    }
    catch (error) {
      console.error(`❌ Failed to update issue #${issueNumber}:`, error)
      throw error
    }
  }

  async closeIssue(issueNumber: number): Promise<void> {
    // Invalidate issue cache since we're modifying data
    this.cache.issues.clear()

    try {
      await this.apiRequestWithRetry(`PATCH /repos/${this.owner}/${this.repo}/issues/${issueNumber}`, {
        state: 'closed',
      })

      console.log(`✅ Closed issue #${issueNumber}`)
    }
    catch (error) {
      console.error(`❌ Failed to close issue #${issueNumber}:`, error)
      throw error
    }
  }

  async unpinIssue(issueNumber: number): Promise<void> {
    try {
      await this.apiRequest(`DELETE /repos/${this.owner}/${this.repo}/issues/${issueNumber}/pin`, undefined)
    }
    catch (error: any) {
      console.log(`⚠️ Failed to unpin issue #${issueNumber}:`, error)
      // Don't throw error for pinning failures as it's not critical
    }
  }

  // Note: GitHub REST API does not support pinning issues programmatically
  // Pinning can only be done manually through the GitHub web interface
  // See: https://docs.github.com/en/issues/tracking-your-work-with-issues/administering-issues/pinning-an-issue-to-your-repository
}
